// Shared pieces of the Sharing pages (contracts/index-cards.md §5): the tier crest, the owner link, breakdown
// tables with an inline bar, the KPI readout, and the date range in the URL.

import type { CSSProperties, ReactNode } from "react";
import { Link, useSearchParams } from "react-router";
import type { ShareOwner } from "../../api/sharing";
import { tableStyles } from "../../components/table";
import { Segmented, TextInput } from "../../components/ui";
import { addDays, daysBetween, todayUtc } from "../../lib/dates";
import { fmtInt, fmtPct } from "../../lib/format";
import { labelOf, tierColor, tierName } from "../../lib/sharing";
import e from "../engagement.module.css";
import pages from "../pages.module.css";
import s from "./sharing.module.css";

// ---------------------------------------------------------------- tier and owner

const tierStyle = (tier: string | null | undefined) => ({ "--tier": tierColor(tier) }) as CSSProperties;

/** The tier's crest mark, the value (when given) and the tier's name. */
export function TierCrest({ tier, value }: { tier: string | null | undefined; value?: number | null }) {
  if (!tier) return <span className={tableStyles.faint}>—</span>;
  return (
    <span className={s.tier} style={tierStyle(tier)} data-tier={tier}>
      <span className={s.crest} aria-hidden />
      {value !== null && value !== undefined ? <span className={s.tierValue}>{value}</span> : null}
      <span className={s.tierName}>{tierName(tier)}</span>
    </span>
  );
}

/** The share's owner: @handle (or the generated "Lumen #4F2A") linking to the player; a deleted player is greyed. */
export function OwnerLink({ owner }: { owner: ShareOwner | null }) {
  if (!owner) {
    return (
      <span className={tableStyles.faint} title="The player's account is gone">
        deleted player
      </span>
    );
  }
  return (
    <Link to={`/players/${encodeURIComponent(owner.userId)}`} className={owner.handle ? s.owner : s.ownerGenerated}>
      {owner.handle ? `@${owner.handle}` : owner.name}
    </Link>
  );
}

// ---------------------------------------------------------------- breakdowns

export interface BreakdownRow {
  key: string;
  clicks: number;
  uniques: number;
  previews?: number;
}

/** Clicks by one dimension: the name with a bar against the biggest row, clicks, uniques and (optionally) previews. */
export function ClickBreakdown({
  title,
  rows,
  labels,
  previews = false,
  testId,
}: {
  title: string;
  rows: BreakdownRow[];
  labels: Record<string, string>;
  previews?: boolean;
  testId: string;
}) {
  const max = Math.max(1, ...rows.map((r) => r.clicks));
  return (
    <div className={s.block}>
      <h3 className={`monoLabel ${s.blockTitle}`}>{title}</h3>
      {rows.length === 0 ? (
        <span className={s.empty}>No clicks in this range.</span>
      ) : (
        <div className={tableStyles.wrap}>
          <table className={tableStyles.table} data-testid={testId}>
            <thead>
              <tr>
                <th>Name</th>
                <th data-align="right">Clicks</th>
                <th data-align="right">Uniques</th>
                {previews ? <th data-align="right">Previews</th> : null}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.key} data-key={r.key}>
                  <td>
                    <span className={s.nameCell}>
                      <span>{labelOf(labels, r.key)}</span>
                      <span className={s.bar} aria-hidden>
                        <span style={{ width: `${(r.clicks / max) * 100}%` }} />
                      </span>
                    </span>
                  </td>
                  <td data-align="right" className="mono">
                    {fmtInt(r.clicks)}
                  </td>
                  <td data-align="right" className="mono">
                    {fmtInt(r.uniques)}
                  </td>
                  {previews ? (
                    <td data-align="right" className="mono">
                      {fmtInt(r.previews ?? 0)}
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** Shares by one dimension: created, completed and the completion rate. */
export function ShareBreakdown({ title, rows, labels, testId }: { title: string; rows: { key: string; created: number; completed: number }[]; labels: Record<string, string>; testId: string }) {
  return (
    <div className={s.block}>
      <h3 className={`monoLabel ${s.blockTitle}`}>{title}</h3>
      {rows.length === 0 ? (
        <span className={s.empty}>No shares in this range.</span>
      ) : (
        <div className={tableStyles.wrap}>
          <table className={tableStyles.table} data-testid={testId}>
            <thead>
              <tr>
                <th>Name</th>
                <th data-align="right">Created</th>
                <th data-align="right">Completed</th>
                <th data-align="right">Rate</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.key} data-key={r.key}>
                  <td>{labelOf(labels, r.key)}</td>
                  <td data-align="right" className="mono">
                    {fmtInt(r.created)}
                  </td>
                  <td data-align="right" className="mono">
                    {fmtInt(r.completed)}
                  </td>
                  <td data-align="right" className="mono">
                    {fmtPct(r.created ? r.completed / r.created : null)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** A table card body for the top lists, or an empty state. */
export function TopTable({ headers, testId, children }: { headers: { label: string; right?: boolean }[]; testId: string; children: ReactNode }) {
  return (
    <div className={tableStyles.wrap} style={{ border: 0, borderRadius: 0 }}>
      <table className={tableStyles.table} data-testid={testId}>
        <thead>
          <tr>
            {headers.map((h) => (
              <th key={h.label} data-align={h.right ? "right" : undefined}>
                {h.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------- numbers

/** A dot-matrix readout fits about four characters: 4,533 → "4.5" k, 18,272 → "18" k. */
export function readout(n: number): { value: string; unit?: string } {
  if (n < 1000) return { value: String(n) };
  if (n < 10_000) return { value: (n / 1000).toFixed(1), unit: "k" };
  if (n < 1_000_000) return { value: String(Math.round(n / 1000)), unit: "k" };
  return { value: (n / 1_000_000).toFixed(1), unit: "M" };
}

export const rate = (a: number, b: number) => (b > 0 ? a / b : null);

// ---------------------------------------------------------------- range (?from&to, UTC days)

export const MAX_DAYS = 180;
export const DEFAULT_DAYS = 28;
const PRESETS = [7, 28, 90] as const;

/** The range in the URL: the last 28 days by default (no params), at most 180 days. */
export function useRange() {
  const [params, setParams] = useSearchParams();
  const today = todayUtc();
  const to = params.get("to") || today;
  const from = params.get("from") || addDays(to, -(DEFAULT_DAYS - 1));
  const span = daysBetween(from, to) + 1;
  const problem = from > to ? "The start is after the end." : span > MAX_DAYS ? `At most ${MAX_DAYS} days at a time.` : null;
  const setRange = (f: string, t: string) => setParams(t === today && f === addDays(today, -(DEFAULT_DAYS - 1)) ? {} : { from: f, to: t }, { replace: true });
  return { from, to, span, today, problem, setRange };
}

export function RangeControl({ range }: { range: ReturnType<typeof useRange> }) {
  const { from, to, today, setRange } = range;
  const preset = to === today ? PRESETS.find((p) => from === addDays(today, -(p - 1))) : undefined;
  return (
    <div className={e.range} data-testid="range">
      <Segmented
        label="Range"
        value={preset ? String(preset) : "custom"}
        onChange={(v) => v !== "custom" && setRange(addDays(today, -(Number(v) - 1)), today)}
        options={[...PRESETS.map((p) => ({ value: String(p), label: `${p} d` })), { value: "custom", label: "Custom", disabled: true }]}
      />
      <TextInput type="date" value={from} max={to} onChange={(ev) => ev.target.value && setRange(ev.target.value, to)} aria-label="From (UTC)" data-testid="range-from" />
      <span className={pages.faint}>–</span>
      <TextInput type="date" value={to} min={from} max={today} onChange={(ev) => ev.target.value && setRange(from, ev.target.value)} aria-label="To (UTC)" data-testid="range-to" />
    </div>
  );
}
